(() => {
  const station = document.getElementById('sv-music-session');
  if (!station) return;
  const audio = station.querySelector('audio');
  const tracks = [...station.querySelectorAll('[data-sv-music-track]')].map(node => ({ src: node.dataset.src, title: node.dataset.title }));
  if (!tracks.length) return;
  const nowPlaying = station.querySelector('[data-sv-now-playing]');
  let trackIndex = 0;
  let playRequest = 0;
  const status = station.querySelector('[role="status"]');
  const loadedScripts = new Set([...document.scripts].filter(s => s.src).map(s => s.src));
  const loadedStyles = new Set([...document.querySelectorAll('link[rel="stylesheet"]')].map(s => s.href));
  let started = false;
  let playingPending = false;
  let navigating = false;
  let queued;
  let bundleManager;
  let bundlePromise;
  let bundleStyles = [];
  const pageListeners = [];
  const eligible = (url) => url.origin === location.origin &&
    !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:checkouts?|account|customer_authentication|authentication|challenge|admin|apps|services|wallets|localization)(?:\/|$)/i.test(url.pathname) &&
    !/\.(?:json|js|css|xml|txt|pdf|zip|mp3|png|jpe?g|webp|svg)$/i.test(url.pathname);
  const syncControls = () => {
    station.querySelectorAll('[data-sv-music-next]').forEach(button => { button.disabled = playingPending; });
    document.querySelectorAll('[data-sv-music-toggle]').forEach(button => {
      button.setAttribute('aria-pressed', String(!audio.paused));
      button.disabled = playingPending;
      const label = button.querySelector('[data-music-label]');
      if (label) label.textContent = audio.paused ? station.dataset.playLabel : station.dataset.pauseLabel;
    });
  };
  const showError = (url) => {
    status.replaceChildren();
    const link = document.createElement('a');
    link.href = url;
    link.textContent = station.dataset.openLabel;
    link.dataset.svFullNavigation = '';
    status.append(link);
  };
  const cleanPage = () => {
    try {
      bundleManager?._saveDraftNow?.();
      bundleManager?.destroyCountdown?.();
      bundleManager?.currentMainSlider?.destroy?.();
      bundleManager?.currentThumbnailSlider?.destroy?.();
    } catch {}
    bundleManager = null;
    for (const [target, type, listener, options] of pageListeners) target.removeEventListener(type, listener, options);
    pageListeners.length = 0;
    bundleStyles.forEach(node => node.remove());
    bundleStyles = [];
    document.getElementById('sv-page-apps')?.remove();
  };
  const installBundleLifecycle = () => {
    const prototype = window.ByobManager?.prototype;
    if (!prototype || prototype.svMusicLifecycle) return;
    const initialize = prototype.initialize;
    const getRedirectUrl = prototype.getRedirectUrl;
    if (getRedirectUrl) prototype.getRedirectUrl = function (...args) {
      const destination = getRedirectUrl.apply(this, args);
      if (started && destination && eligible(new URL(destination, location.href))) {
        queueMicrotask(() => navigate(destination));
        return null;
      }
      return destination;
    };
    prototype.initialize = function (...args) {
      bundleManager = this;
      const headBefore = new Set(document.head.children);
      const originalDocumentAdd = document.addEventListener;
      const originalWindowAdd = window.addEventListener;
      document.addEventListener = function (type, listener, options) {
        pageListeners.push([this, type, listener, options]);
        return originalDocumentAdd.call(this, type, listener, options);
      };
      window.addEventListener = function (type, listener, options) {
        pageListeners.push([this, type, listener, options]);
        return originalWindowAdd.call(this, type, listener, options);
      };
      bundlePromise = Promise.resolve(initialize.apply(this, args)).finally(() => {
        document.addEventListener = originalDocumentAdd;
        window.addEventListener = originalWindowAdd;
        bundleStyles = [...document.head.children].filter(node => node.tagName === 'STYLE' && !headBefore.has(node));
      });
      return bundlePromise;
    };
    prototype.svMusicLifecycle = true;
  };
  const loadScript = (source) => new Promise((resolve, reject) => {
    if (loadedScripts.has(source.src)) { resolve(); return; }
    const script = document.createElement('script');
    for (const attribute of source.attributes) {
      if (!['defer', 'async'].includes(attribute.name)) script.setAttribute(attribute.name, attribute.value);
    }
    script.async = false;
    script.onload = () => {
      loadedScripts.add(source.src);
      installBundleLifecycle();
      resolve();
    };
    script.onerror = () => { script.remove(); reject(new Error('Page script failed')); };
    document.body.append(script);
  });
  const executeScripts = async (roots) => {
    const scripts = roots.flatMap(root => [...root.querySelectorAll('script')]);
    for (const source of scripts) {
      if (source.type && !['text/javascript', 'application/javascript', 'module'].includes(source.type)) continue;
      if (source.src) await loadScript(source);
      else if (source.textContent.trim()) {
        const script = document.createElement('script');
        if (source.type === 'module') script.type = 'module';
        script.textContent = `(() => {\n${source.textContent}\nif (typeof toggleBundleComponents === 'function') window.toggleBundleComponents = toggleBundleComponents;\n})();`;
        source.replaceWith(script);
      }
    }
  };
  const syncMetadata = (doc) => {
    document.title = doc.title;
    const incoming = doc.querySelector('link[rel="canonical"]');
    const current = document.querySelector('link[rel="canonical"]');
    if (incoming && current) current.href = incoming.href;
    for (const selector of ['meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:url"]', 'meta[property="og:description"]', 'meta[property="og:image"]']) {
      const next = doc.querySelector(selector);
      const old = document.querySelector(selector);
      if (next && old) old.setAttribute('content', next.getAttribute('content') || '');
    }
  };
  const rememberScroll = () => history.replaceState({ ...history.state, svMusic: true, svScroll: window.scrollY }, '', location.href);
  const navigate = async (destination, push = true, scrollY = 0) => {
    if (navigating) { queued = { destination, push, scrollY }; return; }
    const url = new URL(destination, location.href);
    if (!eligible(url)) { location.assign(url.href); return; }
    navigating = true;
    status.textContent = station.dataset.loadingLabel;
    document.getElementById('MainContent')?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(20000) });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Page unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextMain = doc.getElementById('MainContent');
      if (!nextMain) throw new Error('Page unavailable');
      const finalUrl = new URL(response.url);
      if (!eligible(finalUrl)) { location.assign(finalUrl.href); return; }
      for (const source of doc.querySelectorAll('link[rel="stylesheet"]')) {
        if (loadedStyles.has(source.href)) continue;
        const link = source.cloneNode(true);
        if (link.media === 'print') link.media = 'all';
        document.head.append(link);
        loadedStyles.add(source.href);
      }
      cleanPage();
      document.querySelector('cart-notification')?.close?.();
      document.querySelector('cart-drawer')?.close?.();
      if (push) {
        rememberScroll();
        history.pushState({ svMusic: true, svScroll: 0 }, '', finalUrl.href + url.hash);
      }
      syncMetadata(doc);
      const appRoot = document.createElement('div');
      appRoot.id = 'sv-page-apps';
      for (const block of doc.querySelectorAll('body > .shopify-block.shopify-app-block')) appRoot.append(block);
      document.getElementById('MainContent').replaceWith(nextMain);
      document.body.append(appRoot);
      const hadBundleClass = Boolean(window.ByobManager);
      const isBundle = [...appRoot.querySelectorAll('script')].some(script => script.textContent.includes('function initializeBYOBBundle'));
      const bundleReady = isBundle ? new Promise((resolve, reject) => {
        const done = () => { clearTimeout(timer); resolve(); };
        const timer = setTimeout(() => {
          document.removeEventListener('bundleInitialized', done);
          reject(new Error('Bundle initialization timed out'));
        }, 20000);
        document.addEventListener('bundleInitialized', done, { once: true });
      }) : null;
      bundlePromise = null;
      await executeScripts([nextMain, appRoot]);
      installBundleLifecycle();
      if (isBundle) {
        if (hadBundleClass) await new window.ByobManager().initialize();
        await bundleReady;
        if (!document.querySelector('.bundlesuite-container .side-cart-checkout-cta')) throw new Error('Bundle unavailable');
      }
      document.querySelectorAll('#MainContent .scroll-trigger--offscreen').forEach(node => node.classList.remove('scroll-trigger--offscreen'));
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document.getElementById('MainContent'));
      document.querySelectorAll('header details[open]').forEach(node => { node.open = false; });
      document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet');
      syncControls();
      document.getElementById('MainContent')?.focus({ preventScroll: true });
      if (url.hash) document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView();
      else window.scrollTo(0, scrollY);
      if (window.StandardEvents?.PageViewEvent) {
        document.dispatchEvent(new window.StandardEvents.PageViewEvent({ page: { template: nextMain.dataset.template, title: document.title, url: location.href } }));
      }
      status.textContent = '';
    } catch (error) { console.warn('[Solvare music] Page transition failed', error); showError(url.href); }
    finally {
      document.getElementById('MainContent')?.removeAttribute('aria-busy');
      navigating = false;
      if (queued) { const next = queued; queued = null; navigate(next.destination, next.push, next.scrollY); }
    }
  };
  audio.volume = 0.25;
  audio.loop = false;
  const selectTrack = (index) => {
    trackIndex = (index + tracks.length) % tracks.length;
    audio.src = tracks[trackIndex].src;
    if (nowPlaying) {
      nowPlaying.hidden = false;
      nowPlaying.textContent = nowPlaying.dataset.label.replace('__title__', tracks[trackIndex].title);
    }
  };
  const play = async () => {
    const request = ++playRequest;
    playingPending = true;
    syncControls();
    try {
      if (!audio.getAttribute('src')) selectTrack(trackIndex);
      await audio.play();
      if (!started) audio.pause();
    } catch { if (request === playRequest && started) status.textContent = station.dataset.errorLabel; }
    finally { if (request === playRequest) { playingPending = false; syncControls(); } }
  };
  const advance = async (resume) => {
    if (playingPending || !started) return;
    status.textContent = '';
    selectTrack(trackIndex + 1);
    if (resume) await play();
    else syncControls();
  };
  const toggle = async () => {
    if (playingPending) return;
    if (!audio.paused) { audio.pause(); return; }
    started = true;
    station.hidden = false;
    document.body.classList.add('sv-music-active');
    status.textContent = '';
    await play();
  };
  for (const event of ['play', 'pause']) audio.addEventListener(event, syncControls);
  audio.addEventListener('ended', () => advance(true));
  audio.addEventListener('error', () => {
    playingPending = false;
    status.textContent = station.dataset.errorLabel;
    syncControls();
  });
  document.addEventListener('click', event => {
    if (event.target.closest?.('[data-sv-music-toggle]')) { event.preventDefault(); toggle(); return; }
    if (event.target.closest?.('[data-sv-music-next]')) { event.preventDefault(); advance(!audio.paused); return; }
    if (event.target.closest?.('[data-sv-music-close]')) {
      started = false; ++playRequest; playingPending = false;
      audio.pause(); station.querySelector('.sv-music-station__panel').open = false; syncControls();
      document.body.classList.remove('sv-music-active');
      return;
    }
    if (!started || window.Shopify?.designMode || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || link.hasAttribute('data-sv-full-navigation') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.href);
    if (!/^https?:$/.test(url.protocol) || !eligible(url)) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
    event.preventDefault();
    navigate(url.href);
  });
  window.addEventListener('popstate', event => {
    if (!started) return;
    navigate(location.href, false, event.state?.svScroll || 0);
  });
  syncControls();
})();
